import React, { useCallback, useState } from "react";
import { UsedCountDetailsCard } from "./used_count_details_card";
import styles from "./portal_membership_space_list.module.css";
import { PortalModal } from "./portal_modal";
import { differenceInDays, format, parseISO } from "date-fns";
import { PortalBookingStep1 } from "./portal_booking_step_1";
import { PortalBookingStep2 } from "./portal_booking_step_2";
import { PortalBookingStep3 } from "./portal_booking_step_3";
import { useToast } from "../../../components";
import { BookingType, PricingType } from "../../../data";
import {
  PortalPathParams,
  usePortalHistory,
} from "../hooks/use_portal_history";
import { Spacer } from "../../spacer";
import { PortalLocationSelect } from "./portal_location_select";
import { useSpaceAvailableDaysQuery } from "../../order_params";
import { getSystemLocale } from "../../../lib/locale/locale";
import { formatCurrency } from "../../../lib/currency/currency";
import { invalidatePartnerAccessCache, useSpaceQuery } from "../../api";
import { useLocationsQuery } from "../hooks/use_locations_query";
import { toGroupLocations } from "../utils/to_group_locations";
import { isNumber } from "lodash";
import { PortalButton } from "./portal_button";
import { useSubmitMembershipBooking } from "../hooks/use_submit_membership_booking";
import { useParams } from "react-router-dom";
import { useSubscriptionOfferingsQuery } from "../hooks/use_subscription_offerings_query";
import { SubscriptionOffering } from "../utils/to_subscription_offerings";
import { useAuth0 } from "@auth0/auth0-react";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import {
  findNextReplenishDateForMembership,
  isSameDay,
  toDay,
} from "../../day_utils";
import { TextLink } from "../../text_link";
import { useAnalytics } from "@/lib/analytics/analytics";
import {
  DateRangePicker,
  PickingRange,
} from "../../../offering_v2/components/date_range_picker/date_range_picker";
import { Button, Card } from "react-bootstrap";
import {
  useGolfSimulationAccessPortalFeatureFlag,
  useUnionHallMeetingRoomsPortalFeatureFlag,
} from "@/core/splitio";

interface PortalMembershipOfferingSpace {
  id: string;
  locationId: string;
  name: string;
  imgUrl: string;
}

interface PortalMembershipSpaceListProps {
  partnerId: string;
  partnerSlug: string;
}

export function PortalMembershipSpaceList({
  partnerId,
  partnerSlug,
}: PortalMembershipSpaceListProps) {
  const { subscriptionId } = useParams<PortalPathParams>();
  const { toMeetingRoomsPage } = usePortalHistory();
  const analytics = useAnalytics();
  const { user } = useAuth0();
  const golfSimulationAccessPortalFeatureFlag =
    useGolfSimulationAccessPortalFeatureFlag(partnerId);

  const [openDialog, setOpenDialog] = useState(false);

  const { data: subscriptionDetails } = useSubscriptionDetailsQuery(
    partnerId,
    subscriptionId
  );
  const unionHallMeetingRoomsFlag = useUnionHallMeetingRoomsPortalFeatureFlag(
    partnerId,
    user?.sub ?? "",
    subscriptionDetails?.membership.membershipPlanId ?? ""
  );
  const { data: offeringsData, mutate } = useSubscriptionOfferingsQuery(
    partnerId,
    subscriptionId
  );

  const isGolfMembershipHartford =
    subscriptionDetails?.membership.id ===
    "17b3d1ce-a996-42fb-aaea-527208315378";

  const offeringsSource: SubscriptionOffering[] = offeringsData || [];

  const offerings = offeringsSource.map((o, i) => ({
    id: i,
    title: o.name,
    description:
      o.usedUnits === o.freeUnits &&
      subscriptionDetails &&
      !!findNextReplenishDateForMembership(subscriptionDetails.startDate)
        ? `You’ve used up your membership passes. They will replenish on ${findNextReplenishDateForMembership(
            subscriptionDetails.startDate
          )}.`
        : o.freeUnits
        ? `Book ${o.freeUnits} membership passes per month.`
        : "With your membership, book day passes with no monthly limit.",
    usedCount: o.usedUnits,
    totalCount: o.freeUnits || undefined,
    isExpired: false,
    spaceType: o.spaceType,
    spaces: o.spaces,
    isUnlimitedPlan: !o.freeUnits,
  }));
  const dayPassOffering = offerings.find(
    (o) => o.spaceType === "entire_location"
  );

  const handleConfirmBooking = (units: number) => {
    if (!offeringsSource) return;

    mutate();
  };

  const handleClickBook = (offering: typeof offerings[0]) => {
    analytics.event("Open Day Pass Booking Modal", {
      "Used Count": offering.usedCount,
      "Remaining Count":
        offering.totalCount && offering.totalCount - offering.usedCount,
      "Subscription UUID": subscriptionId,
      "Partner UUID": partnerId,
      "Unlimited Plan": offering.isUnlimitedPlan,
      "Space Type": offering.spaceType,
      Category: "Membership",
      Source: "Portal",
    });
    if (offering.spaceType === "meeting_room") {
      toMeetingRoomsPage();
    } else {
      setOpenDialog(true);
    }
  };

  return (
    <div>
      <div className={styles.passList}>
        {golfSimulationAccessPortalFeatureFlag && isGolfMembershipHartford && (
          <Card>
            <Card.Img
              variant="top"
              style={{
                objectFit: "cover",
                width: "100%",
                height: 193,
              }}
              src="https://meet-production.s3.amazonaws.com/images/catalog_offering/image/cb43021f-e285-4495-8331-a212389cc340/small_golf3updated.jpg"
            />
            <Card.Body>
              <Card.Title className="fw-semibold">
                Golf Simulation Access
              </Card.Title>
              <Card.Text>
                Sharpen your swing or enjoy a round with friends in our
                state-of-the-art Golf Simulator.
              </Card.Text>
              <Button
                className="w-100"
                variant="primary"
                onClick={() => {
                  window.open(
                    "https://partners.flexspace.ai/offerings/one-state-street-golf-simulation-access/spaces/cfae66ac-8142-455f-973f-06cc42223c12?promoCode=STATESTGOLF",
                    "_blank"
                  );
                }}
              >
                Book now
              </Button>
            </Card.Body>
          </Card>
        )}

        {unionHallMeetingRoomsFlag && (
          <Card>
            <Card.Img
              variant="top"
              style={{
                objectFit: "cover",
                width: "100%",
                height: 193,
              }}
              src="https://meet-production.s3.amazonaws.com/images/space_image/image/215c2341-9b47-4306-8a43-91f1da0b05d4/small_The_Union_Hall_Workspace_mr.jpeg"
            />
            <Card.Body>
              <Card.Title className="fw-semibold">Meeting Rooms</Card.Title>
              <Card.Text>
                Book a meeting room with up to 10 complimentary hours per month
                as part of your membership. Hours reset monthly.
              </Card.Text>
              <Button
                className="w-100"
                variant="primary"
                onClick={() => {
                  window.open(
                    "https://unionhalltx.flexspace.ai/offerings/the-union-hall-workspace-meeting-rooms?promoCode=UNIONHALLMRMEM",
                    "_blank"
                  );
                }}
              >
                Book now
              </Button>
            </Card.Body>
          </Card>
        )}

        {offerings.map((o) => (
          <UsedCountDetailsCard
            key={o.id}
            {...o}
            onClickBook={() => handleClickBook(o)}
            bookingButtonText={
              o.spaceType === "meeting_room"
                ? "Book a meeting room"
                : "Book a day pass"
            }
            bookingDisabled={o.usedCount === o.totalCount}
          />
        ))}
      </div>

      <div className="mt-lg-4 mt-3">
        <span>
          Looking to book other spaces?{" "}
          <TextLink
            href={`/offerings/catalog/${partnerSlug}`}
            textDecoration="none"
          >
            Explore all of our daily offerings
          </TextLink>
        </span>
      </div>

      {openDialog && dayPassOffering && (
        <PortalDaypassBookingDialog
          usedCount={dayPassOffering.usedCount}
          totalCount={dayPassOffering.totalCount}
          spaces={dayPassOffering.spaces}
          onHide={() => setOpenDialog(false)}
          partnerId={partnerId}
          isUnlimitedPlan={dayPassOffering.isUnlimitedPlan}
          onConfirmBooking={handleConfirmBooking}
          subscriptionStartDate={subscriptionDetails?.startDate}
        />
      )}
    </div>
  );
}

interface PortalDaypassBookingDialogProps {
  usedCount: number;
  totalCount?: number;
  spaces: PortalMembershipOfferingSpace[];
  onHide: () => void;
  partnerId: string;
  isUnlimitedPlan?: boolean;
  onConfirmBooking: (bookingUnits: number) => void;
  subscriptionStartDate?: string;
}

enum PortalDaypassBookingDialogStep {
  SelectDate,
  ConfirmBooking,
  BookingConfirmation,
}

function PortalDaypassBookingDialog({
  usedCount,
  totalCount,
  spaces,
  onHide,
  partnerId,
  isUnlimitedPlan,
  onConfirmBooking,
  subscriptionStartDate,
}: PortalDaypassBookingDialogProps) {
  const { subscriptionId } = useParams<PortalPathParams>();
  const toast = useToast();
  const { user } = useAuth0();
  const analytics = useAnalytics();

  const submitBooking = useSubmitMembershipBooking();

  const [selectedCity, setSelectedCity] = useState<string>();
  const [selectedLocationId, setSelectedLocationId] = useState<string>();

  const { data: locationsData } = useLocationsQuery(
    spaces.map((s) => s.locationId)
  );
  const groupLocation = toGroupLocations(locationsData);
  const hasMultipleLocations = groupLocation.length > 1;
  const selectedLocationSpace = hasMultipleLocations
    ? spaces.find((s) => s.locationId === selectedLocationId)
    : spaces[0];

  const [currentStep, setCurrentStep] =
    useState<PortalDaypassBookingDialogStep>(
      PortalDaypassBookingDialogStep.SelectDate
    );
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [loadingConfirmBooking, setLoadingConfirmBooking] = useState(false);

  const { data: space } = useSpaceQuery(selectedLocationSpace?.id);
  const spacePrice = space?.pricings.find(
    (p) => p.type === PricingType.Daily
  )?.price;
  const formatedBasePrice =
    spacePrice && space?.currencyCode
      ? formatCurrency(spacePrice, space.currencyCode, getSystemLocale())
      : "";

  const { availableDays, handleNextVisibleDates, isOutsideRange } =
    useSpaceAvailableDaysQuery(selectedLocationSpace?.id, {
      subscriptionId,
      subscriptionStartDate,
    });

  let formatedBookingDate = startDate && format(startDate, "MMMM dd, yyyy");
  if (startDate && endDate && !isSameDay(toDay(startDate), toDay(endDate))) {
    formatedBookingDate = `${format(startDate, "MMM d, yyyy")} to ${format(
      endDate,
      "MMM d, yyyy"
    )}`;
  }
  let numberOfSelectedDates =
    startDate && endDate
      ? differenceInDays(endDate, startDate) + 1
      : startDate
      ? 1
      : 0;

  const doExceedPasses = usedCount + numberOfSelectedDates > Number(totalCount);
  const disableContinue =
    !startDate || !selectedLocationSpace || doExceedPasses;

  const handleSelectDate = (value: { startDate: Date; endDate: Date }) => {
    setStartDate(value.startDate);
    setEndDate(value.endDate);
  };

  const handleViewDates = useCallback(
    (startDate: Date, endDate: Date) => {
      handleNextVisibleDates(startDate, endDate);
    },
    [handleNextVisibleDates]
  );

  const handleClickContinueToConfirmBooking = () => {
    setCurrentStep(PortalDaypassBookingDialogStep.ConfirmBooking);
    analytics.event("Continue To Confirm Booking", {
      "Used Count": isUnlimitedPlan
        ? numberOfSelectedDates
        : usedCount + numberOfSelectedDates,
      "Remaining Count": isNumber(totalCount)
        ? totalCount - (usedCount + numberOfSelectedDates)
        : undefined,
      "Subscription UUID": subscriptionId,
      "Partner UUID": partnerId,
      Category: "Membership",
      Source: "Portal",
    });
  };

  const handleConfirmBooking = async () => {
    if (selectedLocationSpace && startDate) {
      try {
        setLoadingConfirmBooking(true);
        await submitBooking(partnerId, subscriptionId, {
          spaceId: selectedLocationSpace?.id,
          startDate,
          endDate: endDate || startDate,
        });
        onConfirmBooking(numberOfSelectedDates);
        setLoadingConfirmBooking(false);
        setCurrentStep(PortalDaypassBookingDialogStep.BookingConfirmation);
        analytics.event("Confirm Book Membership Day Pass", {
          "Subscription UUID": subscriptionId,
          "Partner UUID": partnerId,
          Category: "Membership",
          Source: "Portal",
        });
      } catch {
        onHide();
        toast.error({
          message:
            "Fail to confirm booking. Please try later or contact support.",
        });
      }
    }
  };

  const handleCloseDialog = async () => {
    onHide();
    setCurrentStep(PortalDaypassBookingDialogStep.SelectDate);
    if (currentStep === PortalDaypassBookingDialogStep.BookingConfirmation) {
      await invalidatePartnerAccessCache(partnerId);
    }
  };

  const handleClickBackToSelectDate = () => {
    analytics.event("Back To Select Date", {
      "Used Count": isUnlimitedPlan
        ? numberOfSelectedDates
        : usedCount + numberOfSelectedDates,
      "Remaining Count": isNumber(totalCount)
        ? totalCount - (usedCount + numberOfSelectedDates)
        : undefined,
      "Subscription UUID": subscriptionId,
      "Partner UUID": partnerId,
      Category: "Membership",
      Source: "Portal",
    });
    setCurrentStep(PortalDaypassBookingDialogStep.SelectDate);
  };

  const rightContentStep1 = (
    <div>
      {hasMultipleLocations && (
        <div>
          <PortalLocationSelect
            cities={groupLocation.map((l) => ({
              name: l.city,
              locations: l.streetAddresses.map((sA) => ({
                name: sA.name,
                id: sA.locationId,
              })),
            }))}
            value={{ city: selectedCity, locationId: selectedLocationId }}
            onChange={(value) => {
              setSelectedCity(value.city);
              setSelectedLocationId(value.locationId);
              setStartDate(undefined);
              setEndDate(undefined);
            }}
          />
          <Spacer size={24} />
        </div>
      )}
      <div className="border rounded">
        <DateRangePicker
          value={{ startDate, endDate }}
          availableDates={availableDays
            ?.map((d) => parseISO(d.date))
            .filter((d) => !isOutsideRange(d))}
          onChange={handleSelectDate}
          onViewDates={handleViewDates}
          pickingRange={PickingRange.Consecutive}
        />
      </div>
    </div>
  );

  const steps = [
    {
      title: "Select a Date",
      content: (
        <PortalBookingStep1
          title="Day Pass"
          description={
            totalCount
              ? `Book ${totalCount} membership passes per month.`
              : "With your membership, book day passes with no monthly limit."
          }
          warningMessage="Please select within allowance."
          usedCount={usedCount}
          totalCount={totalCount}
          selectedCount={numberOfSelectedDates}
          rightContent={rightContentStep1}
          unitPrice={formatedBasePrice}
          spaceType="entire_location"
        />
      ),
      footer: (
        <div className={styles.step1Footer}>
          <PortalButton
            onClick={handleClickContinueToConfirmBooking}
            disabled={disableContinue}
          >
            Continue
          </PortalButton>
        </div>
      ),
    },
    {
      title: "Confirm Booking",
      content: (
        <PortalBookingStep2
          spaceName="Day Pass"
          capacity={1}
          imgUrl={selectedLocationSpace?.imgUrl || ""}
          location={
            locationsData.find(
              (l) => l.id === selectedLocationSpace?.locationId
            )?.streetAddress || ""
          }
          usedCount={numberOfSelectedDates}
          remainingCount={
            isNumber(totalCount)
              ? totalCount - (usedCount + numberOfSelectedDates)
              : undefined
          }
          bookingDate={formatedBookingDate}
          bookingType={BookingType.Daily}
          isUnlimitedPlan={isUnlimitedPlan}
          cancelPolicy="Cancellable before booking date"
        />
      ),
      footer: (
        <PortalButton
          onClick={handleConfirmBooking}
          disabled={loadingConfirmBooking}
        >
          {loadingConfirmBooking ? "Loading..." : "Confirm booking"}
        </PortalButton>
      ),
    },
    {
      title: "Booking Confirmation",
      content: (
        <PortalBookingStep3
          spaceName="Day Pass"
          capacity={1}
          imgUrl={selectedLocationSpace?.imgUrl || ""}
          location={
            locationsData.find(
              (l) => l.id === selectedLocationSpace?.locationId
            )?.streetAddress || ""
          }
          usedCount={numberOfSelectedDates}
          remainingCount={
            isNumber(totalCount) ? totalCount - usedCount : undefined
          }
          bookingDate={formatedBookingDate}
          bookingEmail={user!.email!}
          bookingType={BookingType.Daily}
          isUnlimitedPlan={isUnlimitedPlan}
        />
      ),
      footer: <PortalButton onClick={handleCloseDialog}>Close</PortalButton>,
    },
  ];

  return (
    <PortalModal
      show={true}
      onHide={handleCloseDialog}
      title={steps[currentStep].title}
      body={steps[currentStep].content}
      footer={steps[currentStep].footer}
      onClickBackButton={
        currentStep === PortalDaypassBookingDialogStep.ConfirmBooking
          ? handleClickBackToSelectDate
          : undefined
      }
    />
  );
}
